import { Switch } from './switch'
import { HTMLSelect } from './html-select'
import { DatePicker } from '@blueprintjs/datetime'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { AmazonAPIState } from '../../util/contstants'
import { isPromise } from '../../util/helpers'
import {
    BulkActionDialog,
    BulkActionDialogActions,
    BulkActionDialogResultActions,
    BulkActionField,
    BulkActionSelectionSummary,
    bulkActionInputClassName,
} from './BulkActionDialog'
import { portfolioSubmissionValue } from './portfolioBulkActions'
import { BulkActionStatus } from './BulkActionStatus'
import {
    notifyBulkActionRunCompleted,
    registerBulkActionRun,
    terminalResultHasMutation,
} from './bulkActionExecution'

const ModalSelectionSummary = ({
    selection,
    selectedCount,
    selectedValues,
    entity = 'item',
    entityPlural,
}) => {
    const count = selectedCount ?? selectedValues?.selectedCount
    const resolvedSelection =
        selection ??
        (typeof count === 'number'
            ? { kind: selectedValues?.all ? 'allMatching' : 'selected', count }
            : null)

    return resolvedSelection ? (
        <BulkActionSelectionSummary
            selection={resolvedSelection}
            entity={entity}
            entityPlural={entityPlural}
        />
    ) : null
}

// The hover background needs horizontal padding, but the control itself must
// stay on the same left edge as every label, input, and legend in the dialog.
// Bleeding the padding outwards with a negative margin gives both.
const ModalRadio = ({ checked, disabled, label, name, onChange, value, autoFocus = false }) => (
    <label className="-mx-[9px] mb-0 flex cursor-pointer items-center gap-[9px] rounded-[5px] px-[9px] py-[8px] text-[14px] text-[#1c2127] hover:bg-[#f6f7f9]">
        <input
            type="radio"
            className="h-[16px] w-[16px] accent-[#2d72d2]"
            checked={checked}
            disabled={disabled}
            name={name}
            onChange={onChange}
            value={value}
            autoFocus={autoFocus}
        />
        <span>{label}</span>
    </label>
)

const ModalRadioGroup = ({ children, label }) => (
    <fieldset className="m-0 mb-[18px] border-0 p-0">
        <legend className="mb-[6px] text-[14px] font-semibold text-[#1c2127]">{label}</legend>
        <div className="flex flex-col gap-[2px]">{children}</div>
    </fieldset>
)

/** Indents a control that belongs to the radio option directly above it. */
const ModalOptionDetail = ({ children }) => (
    <div className="mb-[4px] ml-[25px] mt-[2px]">{children}</div>
)

const LoadingOptions = ({ label = 'Loading options…' }) => (
    <div
        role="status"
        className="flex items-center gap-[10px] py-[18px] text-[14px] text-[#5f6b7c]"
    >
        <span
            aria-hidden="true"
            className="h-[18px] w-[18px] animate-spin rounded-full border-2 border-[#c5d8ee] border-t-[#2d72d2]"
        />
        {label}
    </div>
)

/**
 * `clearable` adds a set/clear radio and submits null when clearing. Opt-in
 * because this modal also drives start date, which has no clear: the column is
 * NOT NULL and Amazon has no equivalent mutation.
 */
export const DateInputModal = ({
    loading,
    name,
    rawName,
    setIsOpen,
    isOpen,
    onSubmit,
    clearable = false,
    clearLabel = 'No end date',
    setLabel = 'Set date',
    selection,
    selectedCount,
    entity = 'item',
    entityPlural,
}) => {
    const [date, setDate] = useState(new Date())
    const [mode, setMode] = useState('set')

    // This modal is mounted for the life of the page and only toggles isOpen,
    // so both pieces of state survive a close. Resetting only `mode` would leave
    // the picker showing a date chosen for some earlier, unrelated selection,
    // ready to be submitted against a new one.
    useEffect(() => {
        if (isOpen) {
            setMode('set')
            setDate(new Date())
        }
    }, [isOpen])

    const clearing = clearable && mode === 'clear'

    const submit = () =>
        onSubmit({
            [keyName(rawName, name)]: clearing ? null : toLocalDateString(date),
        })
    const datePicker = (
        <div className="w-fit rounded-[4px] border border-[#d8e0e8]">
            <DatePicker
                canClearSelection={false}
                disabled={loading}
                value={date}
                onChange={setDate}
            />
        </div>
    )

    return (
        <BulkActionDialog
            isOpen={isOpen}
            onClose={() => setIsOpen(false)}
            dismissible={!loading}
            title={`Change ${name.replace(/^Date /, '').toLowerCase()}`}
            width="compact"
            footer={
                <BulkActionDialogActions
                    primaryLabel={clearing ? 'Clear end date' : 'Apply date'}
                    loadingLabel="Applying…"
                    loading={loading}
                    onCancel={() => setIsOpen(false)}
                    onPrimary={submit}
                />
            }
        >
            <ModalSelectionSummary
                selection={selection}
                selectedCount={selectedCount}
                entity={entity}
                entityPlural={entityPlural}
            />
            {clearable && (
                <ModalRadioGroup label="End date">
                    <ModalRadio
                        checked={mode === 'set'}
                        disabled={loading}
                        label={setLabel}
                        name="date-mode"
                        onChange={(e) => setMode(e.currentTarget.value)}
                        value="set"
                    />
                    {!clearing && <ModalOptionDetail>{datePicker}</ModalOptionDetail>}
                    <ModalRadio
                        checked={mode === 'clear'}
                        disabled={loading}
                        label={clearLabel}
                        name="date-mode"
                        onChange={(e) => setMode(e.currentTarget.value)}
                        value="clear"
                    />
                </ModalRadioGroup>
            )}
            {!clearable && datePicker}
        </BulkActionDialog>
    )
}

export const NumberInputModal = ({
    loading,
    name,
    rawName = null,
    setIsOpen,
    isOpen,
    onSubmit,
    children = null,
    selection,
    selectedCount,
    entity = 'item',
    entityPlural,
}) => {
    const [selected, setSelected] = useState('decrease-%')
    const [value, setValue] = useState('')

    useEffect(() => {
        if (isOpen) {
            setSelected('decrease-%')
            setValue('')
        }
    }, [isOpen])

    const currencySymbol = window.marketplace.currency_symbol
    const isPercentage = selected.endsWith('%')
    const inputAdornment = isPercentage ? '%' : currencySymbol
    const inputLabel = isPercentage ? 'Percentage' : 'Amount'
    const canSubmit = Number(value) > 0

    const submit = () => {
        let resolvedName = rawName || name.toLowerCase()
        let multiplier = 1

        switch (selected) {
            case 'set-to-$':
                multiplier = 100
                break
            case 'increase-$':
            case 'decrease-$':
                resolvedName += '_delta'
                multiplier = 100
                break
            case 'increase-%':
            case 'decrease-%':
                resolvedName += '_percent'
                break
        }

        let v = selected === 'decrease-%' || selected === 'decrease-$' ? -value : value
        v *= multiplier

        onSubmit({
            [resolvedName]: v,
        })
        setValue('')
    }

    const onKeyDown = (e) => (e.key === 'Enter' && canSubmit ? submit() : null)

    return (
        <BulkActionDialog
            isOpen={isOpen}
            onClose={() => setIsOpen(false)}
            dismissible={!loading}
            title={`Change ${name.toLowerCase()}`}
            width="compact"
            footer={
                <BulkActionDialogActions
                    primaryLabel="Apply change"
                    loadingLabel="Applying…"
                    loading={loading}
                    disabled={!canSubmit}
                    onCancel={() => setIsOpen(false)}
                    onPrimary={submit}
                />
            }
        >
            <ModalSelectionSummary
                selection={selection}
                selectedCount={selectedCount}
                entity={entity}
                entityPlural={entityPlural}
            />
            {children}
            <BulkActionField label="Action" htmlFor={`${rawName || name}-action`}>
                <HTMLSelect
                    id={`${rawName || name}-action`}
                    fill
                    large
                    value={selected}
                    onChange={(e) => setSelected(e.target.value)}
                    options={[
                        {
                            label: `Increase ${name.toLowerCase()} (${currencySymbol})`,
                            value: 'increase-$',
                        },
                        { label: `Increase ${name.toLowerCase()} (%)`, value: 'increase-%' },
                        {
                            label: `Set ${name.toLowerCase()} (${currencySymbol})`,
                            value: 'set-to-$',
                        },
                        {
                            label: `Decrease ${name.toLowerCase()} (${currencySymbol})`,
                            value: 'decrease-$',
                        },
                        { label: `Decrease ${name.toLowerCase()} (%)`, value: 'decrease-%' },
                    ]}
                />
            </BulkActionField>
            <BulkActionField
                label={inputLabel}
                htmlFor={`${rawName || name}-value`}
                error={value !== '' && !canSubmit ? 'Enter a value greater than zero.' : undefined}
            >
                <div className="relative">
                    <span className="pointer-events-none absolute inset-y-0 left-0 z-10 flex w-[42px] items-center justify-center border-r border-[#d8e0e8] text-[14px] font-medium text-[#5f6b7c]">
                        {inputAdornment}
                    </span>
                    <input
                        id={`${rawName || name}-value`}
                        disabled={loading}
                        autoFocus
                        min={0}
                        onKeyDown={onKeyDown}
                        placeholder={isPercentage ? 'Enter a percentage' : 'Enter an amount'}
                        value={value}
                        onChange={(e) => setValue(e.target.value)}
                        type="number"
                        className={`${bulkActionInputClassName} pl-[54px]`}
                    />
                </div>
            </BulkActionField>
        </BulkActionDialog>
    )
}

/**
 * MER-2785: a fixed-option select whose value is submitted verbatim.
 *
 * Not DropdownInputModal, which fetches its options from the portfolio endpoint.
 */
export const StaticSelectModal = ({
    loading,
    name,
    rawName,
    options,
    setIsOpen,
    isOpen,
    onSubmit,
    children = null,
    selection,
    selectedCount,
    entity = 'item',
    entityPlural,
}) => {
    const [value, setValue] = useState(options[0].value)

    useEffect(() => {
        if (isOpen) {
            setValue(options[0].value)
        }
    }, [isOpen])

    const submit = () => {
        onSubmit({ [rawName]: value })
    }

    return (
        <BulkActionDialog
            isOpen={isOpen}
            onClose={() => setIsOpen(false)}
            dismissible={!loading}
            title={`Change ${name.toLowerCase()}`}
            width="compact"
            footer={
                <BulkActionDialogActions
                    primaryLabel="Apply change"
                    loadingLabel="Applying…"
                    loading={loading}
                    onCancel={() => setIsOpen(false)}
                    onPrimary={submit}
                />
            }
        >
            <ModalSelectionSummary
                selection={selection}
                selectedCount={selectedCount}
                entity={entity}
                entityPlural={entityPlural}
            />
            {children}
            <BulkActionField label={name} htmlFor={`${rawName}-select`}>
                <HTMLSelect
                    id={`${rawName}-select`}
                    fill
                    large
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    options={options}
                />
            </BulkActionField>
        </BulkActionDialog>
    )
}

/**
 * MER-2785: a plain integer percentage input, 0..900.
 *
 * Deliberately NOT NumberInputModal, which is wrong for placements twice over: it
 * force-appends `_delta`/`_percent` to the field name based on its increase/decrease
 * selector, and multiplies by 100 for its currency modes. That component IS the cents
 * convention; placements are percentage points and must reach the engine undivided.
 *
 * The submitted value is Number()-coerced. Laravel's `integer` rule would accept the
 * string "40", but the engine's Zod schema is strict, so a string would 400 after the
 * queued job had already started.
 */
export const PercentageInputModal = ({
    loading,
    name,
    rawName,
    setIsOpen,
    isOpen,
    onSubmit,
    max = 900,
    children = null,
    selection,
    selectedCount,
    entity = 'item',
    entityPlural,
}) => {
    const [value, setValue] = useState('')

    useEffect(() => {
        if (isOpen) {
            setValue('')
        }
    }, [isOpen])

    const numeric = Number(value)
    const canSubmit = value !== '' && Number.isInteger(numeric) && numeric >= 0 && numeric <= max

    const submit = () => {
        if (!canSubmit) {
            return
        }
        onSubmit({ [rawName]: numeric })
        setValue('')
    }

    const onKeyDown = (e) => (e.key === 'Enter' && canSubmit ? submit() : null)

    return (
        <BulkActionDialog
            isOpen={isOpen}
            onClose={() => setIsOpen(false)}
            dismissible={!loading}
            title={`Change ${name.toLowerCase()}`}
            description={`Enter percentage points from 0 to ${max}. Set 0 to clear the adjustment.`}
            width="compact"
            footer={
                <BulkActionDialogActions
                    primaryLabel="Apply change"
                    loadingLabel="Applying…"
                    loading={loading}
                    disabled={!canSubmit}
                    onCancel={() => setIsOpen(false)}
                    onPrimary={submit}
                />
            }
        >
            <ModalSelectionSummary
                selection={selection}
                selectedCount={selectedCount}
                entity={entity}
                entityPlural={entityPlural}
            />
            {children}
            <BulkActionField
                label="Percentage"
                htmlFor={`${rawName}-percentage`}
                error={
                    value !== '' && !canSubmit
                        ? `Enter a whole number from 0 to ${max}.`
                        : undefined
                }
            >
                <div className="relative">
                    <span className="pointer-events-none absolute inset-y-0 left-0 z-10 flex w-[42px] items-center justify-center border-r border-[#d8e0e8] text-[14px] font-medium text-[#5f6b7c]">
                        %
                    </span>
                    <input
                        id={`${rawName}-percentage`}
                        disabled={loading}
                        autoFocus
                        min={0}
                        max={max}
                        step={1}
                        onKeyDown={onKeyDown}
                        placeholder="Enter a percentage"
                        value={value}
                        onChange={(e) => setValue(e.target.value)}
                        type="number"
                        className={`${bulkActionInputClassName} pl-[54px]`}
                    />
                </div>
            </BulkActionField>
        </BulkActionDialog>
    )
}

export const RadioInputModal = ({
    loading,
    name,
    rawName = null,
    setIsOpen,
    isOpen,
    onSubmit,
    selection,
    selectedCount,
    entity = 'item',
    entityPlural,
}) => {
    const [selected, setSelected] = useState(AmazonAPIState.PAUSE)

    useEffect(() => {
        if (isOpen) {
            setSelected(AmazonAPIState.PAUSE)
        }
    }, [isOpen])

    const submit = () => onSubmit({ [keyName(rawName, name)]: selected })

    return (
        <BulkActionDialog
            isOpen={isOpen}
            onClose={() => setIsOpen(false)}
            dismissible={!loading}
            title={`Change ${name.toLowerCase()}`}
            width="compact"
            footer={
                <BulkActionDialogActions
                    primaryLabel="Apply state"
                    loadingLabel="Applying…"
                    loading={loading}
                    onCancel={() => setIsOpen(false)}
                    onPrimary={submit}
                />
            }
        >
            <ModalSelectionSummary
                selection={selection}
                selectedCount={selectedCount}
                entity={entity}
                entityPlural={entityPlural}
            />
            <ModalRadioGroup label="State">
                {[
                    ['Enabled', AmazonAPIState.RESUME],
                    ['Paused', AmazonAPIState.PAUSE],
                    ['Archived', AmazonAPIState.ARCHIVE],
                ].map(([label, value], index) => (
                    <ModalRadio
                        key={value}
                        autoFocus={index === 0}
                        checked={selected === value}
                        disabled={loading}
                        label={label}
                        name="states"
                        onChange={(e) => setSelected(parseInt(e.target.value, 10))}
                        value={value}
                    />
                ))}
            </ModalRadioGroup>
        </BulkActionDialog>
    )
}

export const TargetAcosModal = ({
    loading,
    name,
    rawName,
    setIsOpen,
    isOpen,
    onSubmit,
    selection,
    selectedCount,
    entity = 'item',
    entityPlural,
}) => {
    const [type, setType] = useState('number')
    const [value, setValue] = useState('')

    useEffect(() => {
        if (isOpen) {
            setType('number')
            setValue('')
        }
    }, [isOpen])

    // Without this guard an empty field submitted `Number('') * 100`, silently
    // setting every selected item's target ACoS to 0%.
    const removing = type === 'delete'
    const canSubmit = removing || (value !== '' && Number(value) > 0)

    const submit = () => {
        if (!canSubmit) {
            return
        }

        onSubmit({
            [keyName(rawName, name)]: targetAcosSubmissionValue(type, value),
        })
    }

    return (
        <BulkActionDialog
            isOpen={isOpen}
            onClose={() => setIsOpen(false)}
            dismissible={!loading}
            title="Change target ACoS"
            width="compact"
            footer={
                <BulkActionDialogActions
                    primaryLabel={removing ? 'Remove target ACoS' : 'Apply target ACoS'}
                    loadingLabel="Applying…"
                    loading={loading}
                    disabled={!canSubmit}
                    onCancel={() => setIsOpen(false)}
                    onPrimary={submit}
                />
            }
        >
            <ModalSelectionSummary
                selection={selection}
                selectedCount={selectedCount}
                entity={entity}
                entityPlural={entityPlural}
            />
            <ModalRadioGroup label="Action">
                <ModalRadio
                    autoFocus
                    checked={type === 'number'}
                    disabled={loading}
                    label="Set target ACoS"
                    name="target-acos-type"
                    onChange={(e) => setType(e.target.value)}
                    value="number"
                />
                {!removing && (
                    <ModalOptionDetail>
                        <BulkActionField
                            label="Target ACoS"
                            htmlFor="target-acos-value"
                            error={
                                value !== '' && !canSubmit
                                    ? 'Enter a percentage greater than zero.'
                                    : undefined
                            }
                        >
                            <div className="relative">
                                <span className="pointer-events-none absolute inset-y-0 left-0 z-10 flex w-[42px] items-center justify-center border-r border-[#d8e0e8] text-[14px] font-medium text-[#5f6b7c]">
                                    %
                                </span>
                                <input
                                    id="target-acos-value"
                                    disabled={loading}
                                    autoFocus
                                    min={0}
                                    aria-invalid={value !== '' && !canSubmit ? 'true' : undefined}
                                    onKeyDown={(e) =>
                                        e.key === 'Enter' && canSubmit ? submit() : null
                                    }
                                    placeholder="Enter percentage"
                                    value={value}
                                    onChange={(e) => setValue(e.target.value)}
                                    type="number"
                                    className={`${bulkActionInputClassName} pl-[54px]`}
                                />
                            </div>
                        </BulkActionField>
                    </ModalOptionDetail>
                )}
                <ModalRadio
                    checked={type === 'delete'}
                    disabled={loading}
                    label="Remove target ACoS"
                    name="target-acos-type"
                    onChange={(e) => setType(e.target.value)}
                    value="delete"
                />
            </ModalRadioGroup>
        </BulkActionDialog>
    )
}

export const targetAcosSubmissionValue = (type, value) => {
    if (type === 'delete') {
        return 'target_acos_delete'
    }

    const scaled = Number(value) * 100
    const rounded = scaled < 0 ? -Math.round(-scaled) : Math.round(scaled)

    // PHP's historical `(int) round(...)` normalizes negative zero to zero.
    return Object.is(rounded, -0) ? 0 : rounded
}

const keyName = (rawName, name) => rawName || name.toLowerCase()

/**
 * The picker hands back midnight *local* time, and JSON.stringify serialises a
 * Date as UTC. Sending the Date shifts the calendar day for every user east of
 * UTC: midnight on the 30th in Berlin is 22:00 on the 29th in UTC, and both the
 * engine and Carbon then read the 29th. The user picked a calendar date, not an
 * instant, so send one.
 */
export const toLocalDateString = (date) => {
    const pad = (n) => String(n).padStart(2, '0')
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

export const DropdownInputModal = ({
    loading,
    name,
    rawName,
    setIsOpen,
    isOpen,
    onSubmit,
    fetchData,
    selectedValues,
    selection,
    selectedCount,
    entity = 'item',
    entityPlural,
    allowUnassigned = false,
}) => {
    const [value, setValue] = useState(0)
    // null while the options are still loading. An empty array is a legitimate
    // answer (a profile with no Amazon portfolios) and must still render.
    const [data, setData] = useState(null)

    useEffect(() => {
        if (!isOpen) {
            return
        }

        setData(null)

        const applyOptions = (options) => {
            setData(options)
            if (options.length > 0) {
                setValue(options[0].id)
            } else if (allowUnassigned) {
                setValue('')
            }
        }

        // Table widgets render this modal without wiring a portfolio fetcher, so
        // `fetchData` can be undefined. Degrade to an empty dialog: calling it
        // crashed the widget tree, and bailing out left the spinner up forever.
        const promise = typeof fetchData === 'function' ? fetchData() : null
        if (!isPromise(promise)) {
            applyOptions([])
            return
        }

        promise
            .then((res) => {
                applyOptions(Array.isArray(res?.data?.data) ? res.data.data : [])
            })
            // The shared axios handler has already toasted the failure. Render the
            // dialog empty rather than spinning forever with no way out.
            .catch(() => applyOptions([]))
    }, [isOpen])

    const renderPortfolios = () => {
        const output = []
        if (allowUnassigned) {
            output.push(
                <option key="unassigned" value="">
                    No portfolio
                </option>,
            )
        }
        data.forEach((v, i) => {
            output.push(
                <option key={i} value={v.id}>
                    {v.name}
                </option>,
            )
        })

        return output
    }

    const noOptions = data !== null && data.length === 0 && !allowUnassigned
    const submit = () =>
        onSubmit({
            [rawName || name.toLowerCase()]: portfolioSubmissionValue(value, allowUnassigned),
        })

    return (
        <BulkActionDialog
            isOpen={isOpen}
            onClose={() => setIsOpen(false)}
            dismissible={!loading}
            title={name}
            width="compact"
            footer={
                <BulkActionDialogActions
                    primaryLabel="Apply portfolio"
                    loadingLabel="Applying…"
                    loading={loading}
                    disabled={data === null || noOptions}
                    onCancel={() => setIsOpen(false)}
                    onPrimary={submit}
                />
            }
        >
            <ModalSelectionSummary
                selection={selection}
                selectedCount={selectedCount}
                selectedValues={selectedValues}
                entity={entity}
                entityPlural={entityPlural}
            />
            {data === null ? (
                <LoadingOptions label="Loading portfolios…" />
            ) : noOptions ? (
                <div className="rounded-[6px] border border-[#d8e0e8] bg-[#f6f7f9] px-[14px] py-[16px] text-[14px] leading-[20px] text-[#5f6b7c]">
                    No portfolios are available for this advertising profile.
                </div>
            ) : (
                <BulkActionField label="Portfolio" htmlFor="bulk-action-portfolio">
                    <HTMLSelect
                        id="bulk-action-portfolio"
                        fill={true}
                        value={value}
                        onChange={(e) => setValue(e.target.value)}
                    >
                        {renderPortfolios()}
                    </HTMLSelect>
                </BulkActionField>
            )}
        </BulkActionDialog>
    )
}

/**
 * The negative-keyword table pulls in the full table/column graph. Load it only
 * when this bespoke modal is actually requested so the standard modal stories
 * remain deterministic and avoid the table graph's circular module dependency.
 */
export const NegativeKeywordModal = (props) => {
    const [dependencies, setDependencies] = useState(null)

    useEffect(() => {
        let mounted = true
        Promise.all([
            import('change-case'),
            import('react-redux'),
            import('../../columns'),
            import('../../config'),
            import('../../hooks/useInterval'),
            import('../../packages/blueprint-components/src/common'),
            import('../../reducers/index'),
            import('../../util/api'),
            import('../amazon_link'),
            import('../table/performance_columns.ts'),
            import('./Table'),
        ]).then(
            ([
                changeCase,
                reactRedux,
                columns,
                config,
                interval,
                common,
                reducers,
                api,
                amazonLink,
                performanceColumns,
                table,
            ]) => {
                if (mounted) {
                    setDependencies({
                        capitalCase: changeCase.capitalCase,
                        useDispatch: reactRedux.useDispatch,
                        useSelector: reactRedux.useSelector,
                        Columns: columns.Columns,
                        KeywordMatchTypes: config.KeywordMatchTypes,
                        useInterval: interval.useInterval,
                        DefaultSelected: common.DefaultSelected,
                        fetchBulkActionProgress: reducers.fetchBulkActionProgress,
                        axiosGet: api.axiosGet,
                        isAsin: amazonLink.isAsin,
                        PerformanceColumns: performanceColumns.PerformanceColumns,
                        Table: table.Table,
                    })
                }
            },
        )

        return () => {
            mounted = false
        }
    }, [])

    return dependencies ? (
        <NegativeKeywordModalImplementation {...props} dependencies={dependencies} />
    ) : (
        <BulkActionDialog
            isOpen={!!props.searchTerm}
            onClose={() => props.onClose?.()}
            title="Create negative keyword or target"
            width="wide"
        >
            <LoadingOptions label="Loading selected ad groups…" />
        </BulkActionDialog>
    )
}

export const NegativeKeywordLifecycleStatus = ({ phase, type, progress, result }) => {
    if (phase === 'running') {
        return (
            <BulkActionStatus
                phase="running"
                title={`Creating negative ${type}s`}
                progressPercent={
                    typeof progress?.progress === 'number' ? progress.progress : undefined
                }
                counts={
                    progress
                        ? {
                              completed: progress.update_count,
                              total: progress.total_items_count,
                          }
                        : undefined
                }
            />
        )
    }

    const succeeded = result?.counts?.success ?? 0
    const failed = result?.counts?.failed ?? 0

    return (
        <BulkActionStatus
            phase={phase}
            verb="created"
            entity={`negative ${type}`}
            entityPlural={`negative ${type}s`}
            title={
                phase === 'succeeded'
                    ? `Negative ${type}s created`
                    : phase === 'partially_succeeded'
                      ? `Some negative ${type}s were created`
                      : `Negative ${type}s were not created`
            }
            message={result?.message}
            counts={{ succeeded, failed }}
        />
    )
}

export const NegativeKeywordModalImplementation = ({
    loading,
    onClose = null,
    searchTerm = null,
    setSearchTerm = null,
    kind,
    onSubmit,
    reloadParent = () => {},
    dependencies,
}) => {
    const {
        capitalCase,
        useDispatch,
        useSelector,
        Columns,
        KeywordMatchTypes,
        useInterval,
        DefaultSelected,
        fetchBulkActionProgress,
        axiosGet,
        isAsin,
        PerformanceColumns,
        Table,
    } = dependencies
    const dispatch = useDispatch()
    const bulkActionProgress = useSelector((store) => store.progress.bulk_actions)
    const [matchType, setMatchType] = useState(KeywordMatchTypes.Exact)
    const [keyword, setKeyword] = useState('')
    const [maxWords, setMaxWords] = useState(1)
    const [selected, setSelected] = useState(DefaultSelected)
    const [invalidMessage, setInvalidMessage] = useState('')
    const [phase, setPhase] = useState('configuring')
    const [activeUuid, setActiveUuid] = useState(null)
    const [terminalResult, setTerminalResult] = useState(null)
    const [submitError, setSubmitError] = useState('')
    const unregisterRun = useRef(null)
    const st = searchTerm?.search_term || ''
    let type = kind || 'keyword'
    if (!kind && st != '') {
        type = isAsin(searchTerm?.search_term) ? 'target' : 'keyword'
    }

    const [exists, setExists] = useState({})
    const [existsLoading, setExistsLoading] = useState(false)

    useInterval(() => {
        if (!activeUuid) {
            return
        }

        dispatch(fetchBulkActionProgress()).then((finishedUuids) => {
            finishedUuids.forEach(({ uuid }) => notifyBulkActionRunCompleted(uuid))
        })
    }, 1000)

    useEffect(
        () => () => {
            unregisterRun.current?.()
        },
        [],
    )

    useEffect(() => {
        setKeyword(searchTerm?.search_term)
        setSelected(DefaultSelected)
        setMatchType(KeywordMatchTypes.Exact)
        setPhase('configuring')
        setActiveUuid(null)
        setTerminalResult(null)
        setSubmitError('')
        unregisterRun.current?.()
        unregisterRun.current = null
        calculateMaxWords(type)
    }, [searchTerm])

    useEffect(() => {
        calculateMaxWords(type)
    }, [matchType])

    const findExisting = (searchTerm, matchType, type, adGroupIds) => {
        setExistsLoading(true)
        axiosGet('search-terms/existing', {
            ad_group_ids: adGroupIds,
            search_term: searchTerm,
            match_type: matchType,
            type: type,
        })
            .then((res) => {
                setExistsLoading(false)
                setExists(res.data)
            })
            .catch(() => {
                setExistsLoading(false)
            })
    }

    useEffect(() => {
        if (searchTerm) {
            findExisting(
                searchTerm?.search_term,
                matchType,
                type,
                searchTerm?.performances?.map((i) => i.ad_group_id),
            )
        }
    }, [searchTerm, matchType, keyword])

    const handleTerminalRun = (uuid, outcome) => {
        const terminalRequest = outcome?.response
            ? Promise.resolve(outcome.response)
            : outcome?.error
              ? Promise.reject(outcome.error)
              : axiosGet('bulk-actions/run-info', { uuid })
        terminalRequest
            .then((res) => {
                const result = res?.data ?? {}
                const hasSucceededCount = typeof result?.counts?.success === 'number'
                const succeeded = hasSucceededCount ? result.counts.success : 0
                const failed = typeof result?.counts?.failed === 'number' ? result.counts.failed : 0
                const hasMutation = terminalResultHasMutation(result)
                const terminalPhase =
                    hasSucceededCount && succeeded > 0 && failed > 0
                        ? 'partially_succeeded'
                        : hasMutation
                          ? 'succeeded'
                          : 'failed'

                if (!outcome?.refreshed && hasMutation) {
                    reloadParent()
                }

                if (outcome?.presented) {
                    setTerminalResult(result)
                    setActiveUuid(null)
                    setPhase(terminalPhase)
                    return
                }

                setTerminalResult(result)
                setPhase(terminalPhase)
                setActiveUuid(null)
            })
            .catch((err) => {
                // A terminal refusal (403) carries the customer text in `message`.
                const serverMessage = err?.response?.data?.message
                setTerminalResult({
                    counts: { success: 0, failed: selected.selectedCount },
                    message:
                        typeof serverMessage === 'string' && serverMessage !== ''
                            ? serverMessage
                            : 'We could not load the result for this bulk action.',
                })
                setPhase('failed')
                setActiveUuid(null)
            })
            .finally(() => {
                unregisterRun.current?.()
                unregisterRun.current = null
            })
    }

    const submit = () => {
        setPhase('submitting')
        setSubmitError('')
        const submission = onSubmit({
            selection: selected,
            update: {
                negative_keyword: {
                    keyword,
                    type,
                    match_type: matchType,
                },
            },
            class: 'AdGroups',
        })

        Promise.resolve(submission)
            .then((res) => {
                const progressEntries = Object.values(res?.data?.progress ?? {})
                const progressPending = res?.data?.progress_pending === true
                const uuid = progressPending
                    ? res?.data?.request_uuid
                    : progressEntries
                          .map((entry) => entry?.uuid ?? entry?.extra?.uuid)
                          .find((value) => typeof value === 'string')

                if (!uuid) {
                    setPhase('configuring')
                    setSubmitError(
                        'The bulk action started without a progress ID. Please refresh and try again.',
                    )
                    return
                }

                unregisterRun.current?.()
                unregisterRun.current = registerBulkActionRun(
                    uuid,
                    handleTerminalRun,
                    progressPending,
                )
                setActiveUuid(uuid)
                setPhase('running')
            })
            .catch(() => {
                setPhase('configuring')
                setSubmitError(
                    'The negative keyword could not be submitted. Check the form and try again.',
                )
            })
    }

    const text = selected.selectedCount
        ? `Create ${selected.selectedCount} negative ${type}s`
        : `Create negative ${type}s`

    const onChange = (e) => {
        const type = e.target.name

        setKeyword(e.target.value)
        calculateMaxWords(type, e.target.value)
    }

    const calculateMaxWords = (type, kw) => {
        kw = kw || keyword
        if (type !== 'keyword') {
            setInvalidMessage(
                kw?.split(' ').length > 1 ? 'Target cannot be more than one word' : '',
            )
            setMaxWords(1)
            return
        }

        if (matchType == KeywordMatchTypes.Exact) {
            setInvalidMessage(
                kw?.split(' ').length > 10
                    ? 'Exact match keywords cannot be more than ten words'
                    : '',
            )
            setMaxWords(10)
        } else if (matchType == KeywordMatchTypes.Phrase) {
            setInvalidMessage(
                kw?.split(' ').length > 4
                    ? 'Phrase match keywords cannot be more than four words'
                    : '',
            )
            setMaxWords(4)
        }
    }

    const labelWording = (
        <span>
            Negative {capitalCase(type)}{' '}
            <span className="text-gray-500">
                (max {maxWords} {maxWords == 1 ? 'word' : 'words'})
            </span>
        </span>
    )

    const ownedProgress = useMemo(() => {
        if (!activeUuid) {
            return null
        }

        return Object.values(bulkActionProgress ?? {}).find(
            (entry) => entry?.uuid === activeUuid || entry?.extra?.uuid === activeUuid,
        )
    }, [activeUuid, bulkActionProgress])

    const terminal = ['succeeded', 'partially_succeeded', 'failed'].includes(phase)
    const submitting = phase === 'submitting' || loading
    const close = () => {
        setSearchTerm?.(null)
        onClose?.()
    }

    return (
        <BulkActionDialog
            isOpen={!!searchTerm}
            onClose={close}
            dismissible={!submitting && phase !== 'running'}
            title={terminal ? `Negative ${type} results` : `Create negative ${type}s`}
            width="wide"
            footer={
                terminal ? (
                    <BulkActionDialogResultActions onDismiss={close} />
                ) : phase === 'running' ? undefined : (
                    <BulkActionDialogActions
                        primaryLabel={text}
                        loadingLabel="Creating…"
                        loading={submitting}
                        disabled={selected.selectedCount === 0 || invalidMessage !== ''}
                        onCancel={close}
                        onPrimary={submit}
                    />
                )
            }
        >
            {phase === 'running' ? (
                <NegativeKeywordLifecycleStatus
                    phase="running"
                    type={type}
                    progress={ownedProgress}
                />
            ) : terminal ? (
                <NegativeKeywordLifecycleStatus phase={phase} type={type} result={terminalResult} />
            ) : (
                <>
                    <ModalSelectionSummary
                        selectedCount={selected.selectedCount}
                        entity="ad group"
                        entityPlural="ad groups"
                    />
                    {submitError && (
                        <div
                            role="alert"
                            className="mb-[18px] rounded-[6px] border border-[#e8b7b9] bg-[#fff3f3] px-[14px] py-[12px] text-[14px] text-[#a82a2f]"
                        >
                            {submitError}
                        </div>
                    )}
                    <div className="grid gap-[16px] md:grid-cols-[180px_1fr]">
                        {type === 'keyword' ? (
                            <ModalRadioGroup label="Match type">
                                <ModalRadio
                                    checked={matchType === KeywordMatchTypes.Phrase}
                                    label="Phrase"
                                    name="negative-match-type"
                                    onChange={(e) => setMatchType(parseInt(e.target.value, 10))}
                                    value={KeywordMatchTypes.Phrase}
                                />
                                <ModalRadio
                                    checked={matchType === KeywordMatchTypes.Exact}
                                    label="Exact"
                                    name="negative-match-type"
                                    onChange={(e) => setMatchType(parseInt(e.target.value, 10))}
                                    value={KeywordMatchTypes.Exact}
                                />
                            </ModalRadioGroup>
                        ) : (
                            <ModalRadioGroup label="Match type">
                                <ModalRadio
                                    checked
                                    label="Target"
                                    name="negative-match-type"
                                    onChange={(e) => setMatchType(parseInt(e.target.value, 10))}
                                    value={KeywordMatchTypes.Exact}
                                />
                            </ModalRadioGroup>
                        )}
                        <BulkActionField
                            label={labelWording}
                            htmlFor="negative-keyword-input"
                            error={invalidMessage || undefined}
                        >
                            <input
                                id="negative-keyword-input"
                                name={type}
                                value={keyword || ''}
                                aria-invalid={invalidMessage ? 'true' : undefined}
                                onChange={onChange}
                                className={bulkActionInputClassName}
                            />
                        </BulkActionField>
                    </div>
                    {existsLoading && <LoadingOptions label="Checking existing negatives…" />}
                    <div className="overflow-x-auto rounded-[6px] border border-[#d8e0e8]">
                        <Table
                            canSelectFromAllPages={false}
                            name="negative-keyword-modal"
                            columnSelection={false}
                            tableHooks={{
                                items: searchTerm?.performances
                                    ? searchTerm.performances.map((i) => ({
                                          ...i,
                                          exists: exists[i.ad_group_id],
                                      }))
                                    : [],
                                reload: () => {},
                                pagination: {
                                    total: searchTerm?.performances?.length,
                                    from: searchTerm?.performances?.length,
                                    to: searchTerm?.performances?.length,
                                },
                            }}
                            columns={[
                                {
                                    key: 'search_term',
                                    title: 'Breakdown',
                                    cell: (searchTerm) => (
                                        <div className="flex justify-start">
                                            {Columns.AdGroupName.cell(searchTerm?.ad_group, false)}
                                        </div>
                                    ),
                                },
                                {
                                    key: ' ',
                                    title: 'Exists',
                                    cell: (searchTerm) => (
                                        <div className="flex flex-col">
                                            {searchTerm.exists?.campaign_negative_keywords?.map(
                                                (i) => (
                                                    <span key={i.keyword_id}>
                                                        Campaign Negative{' '}
                                                        {matchTypeToString(
                                                            i.match_type,
                                                            KeywordMatchTypes,
                                                        )}{' '}
                                                        Keyword
                                                    </span>
                                                ),
                                            )}
                                            {searchTerm.exists?.negative_keywords?.map((i) => (
                                                <span key={i.keyword_id}>
                                                    Negative{' '}
                                                    {matchTypeToString(
                                                        i.match_type,
                                                        KeywordMatchTypes,
                                                    )}{' '}
                                                    Keyword
                                                </span>
                                            ))}
                                            {searchTerm.exists?.negative_targets?.map((i) => (
                                                <span key={i.target_id}>Negative Target</span>
                                            ))}
                                        </div>
                                    ),
                                },
                                ...PerformanceColumns,
                            ]}
                            selectKey="ad_group_id"
                            selectedValues={selected}
                            onSelectChange={setSelected}
                        />
                    </div>
                </>
            )}
        </BulkActionDialog>
    )
}

export const DeleteModal = ({
    loading,
    name,
    rawName,
    setIsOpen,
    isOpen,
    onSubmit,
    rawKey = '',
    selection,
    selectedCount,
    entity: entityProp,
    entityPlural: entityPluralProp,
}) => {
    const legacyEntityPlural = rawName ?? name.toLowerCase()
    const entity =
        entityProp ??
        (legacyEntityPlural.endsWith('s') ? legacyEntityPlural.slice(0, -1) : legacyEntityPlural)
    const entityPlural = entityPluralProp ?? legacyEntityPlural
    const submit = () => onSubmit({ [rawKey || `${rawName}_delete`]: true })

    return (
        <BulkActionDialog
            isOpen={isOpen}
            onClose={() => setIsOpen(false)}
            dismissible={!loading}
            title={name}
            description={`This cannot be undone. Existing automation and history for these ${entityPlural} may no longer be available.`}
            width="compact"
            footer={
                <BulkActionDialogActions
                    // `rawName` is an API key, not display copy.
                    primaryLabel={`Delete ${entityPlural}`}
                    loadingLabel="Deleting…"
                    loading={loading}
                    destructive
                    onCancel={() => setIsOpen(false)}
                    onPrimary={submit}
                />
            }
        >
            <ModalSelectionSummary
                selection={selection}
                selectedCount={selectedCount}
                entity={entity}
                entityPlural={entityPlural}
            />
        </BulkActionDialog>
    )
}

export const TestBulkRunModal = ({
    loading,
    name,
    rawName,
    setIsOpen,
    isOpen,
    onSubmit,
    rawKey = '',
    selection,
    selectedCount,
    entity: entityProp,
    entityPlural: entityPluralProp,
}) => {
    // Determine entity name from name prop (e.g., "Bulk Run Recipes" -> "recipes", "Bulk Run Segments" -> "segments")
    const entityName = name.toLowerCase().includes('segment') ? 'segments' : 'recipes'
    const entity = entityProp ?? entityName.slice(0, -1)
    const entityPlural = entityPluralProp ?? entityName

    const submit = () => onSubmit({ [rawKey || `${rawName}_bulk_run`]: true })

    return (
        <BulkActionDialog
            isOpen={isOpen}
            onClose={() => setIsOpen(false)}
            dismissible={!loading}
            title={name}
            width="compact"
            footer={
                <BulkActionDialogActions
                    primaryLabel={`Run ${entityName}`}
                    loadingLabel="Starting…"
                    loading={loading}
                    onCancel={() => setIsOpen(false)}
                    onPrimary={submit}
                />
            }
        >
            <ModalSelectionSummary
                selection={selection}
                selectedCount={selectedCount}
                entity={entity}
                entityPlural={entityPlural}
            />
            <p className="m-0 text-[14px] leading-[20px] text-[#5f6b7c]">
                This starts a bulk run using each {entity}'s current configuration.
            </p>
        </BulkActionDialog>
    )
}

export const BooleanModal = ({
    loading,
    label,
    name,
    rawName,
    setIsOpen,
    isOpen,
    onSubmit,
    children,
    selection,
    selectedCount,
    entity = 'item',
    entityPlural,
}) => {
    const [enabled, setEnabled] = useState(false)

    const submit = (v) =>
        onSubmit({
            [rawName || name.toLowerCase()]: typeof v !== 'undefined' ? v : enabled,
        })

    useEffect(() => {
        if (isOpen) {
            setEnabled(false)
        }
    }, [isOpen])

    return (
        <BulkActionDialog
            isOpen={isOpen}
            onClose={() => setIsOpen(false)}
            dismissible={!loading}
            title={name.replace(/^Toggle /, 'Change ')}
            width="compact"
            footer={
                <BulkActionDialogActions
                    primaryLabel="Apply setting"
                    loadingLabel="Applying…"
                    loading={loading}
                    onCancel={() => setIsOpen(false)}
                    onPrimary={() => submit()}
                />
            }
        >
            <ModalSelectionSummary
                selection={selection}
                selectedCount={selectedCount}
                entity={entity}
                entityPlural={entityPlural}
            />
            {children}
            <div className="rounded-[6px] border border-[#d8e0e8] bg-[#f6f7f9] px-[14px] py-[13px]">
                <Switch
                    disabled={loading}
                    label={label}
                    large
                    checked={enabled}
                    onChange={(e) => setEnabled(e.target.checked)}
                />
            </div>
        </BulkActionDialog>
    )
}

const matchTypeToString = (matchType, KeywordMatchTypes) => {
    switch (matchType) {
        case KeywordMatchTypes.Exact:
            return 'Exact'
        case KeywordMatchTypes.Phrase:
            return 'Phrase'
        case KeywordMatchTypes.Broad:
            return 'Broad'
        default:
            console.error('Cannot resolve match type for:', matchType, typeof matchType)
    }
}
